import React, { useState, useRef, useEffect } from "react";
import { Editor } from '@tinymce/tinymce-react';
import { Editor as TinyMCEEditor } from 'tinymce';
import { TINYMCE_API_KEY } from 'src/utils/tinymce';

import { IClub } from "@eagerdog/interfaces";
import { apiService } from "../../services/api.service";
import { toast } from "../Toast/ToastManager";

import ImageUploader from "../ImageUploader/ImageUploader";
import Button from "../Button/Button";

import "./ClubSettings.scss";

interface IProps {
  club: IClub;
  onClubUpdate: (club: IClub) => void;
  clubId?: string; // Optional for backward compatibility
}

const ClubSettings: React.FC<IProps> = ({ club, onClubUpdate, clubId }) => {
  const descriptionEditorRef = useRef<TinyMCEEditor | null>(null);
  
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  
  // Customization state
  const [description, setDescription] = useState<string>(club.description || "");

  useEffect(() => {
    setDescription(club.description || "");
  }, [club]);

  const handleLogoChange = async (file: File | null) => {
    if (file) {
      try {
        const logoUrl = await uploadImage(file, 'logo');
        
        // Update club in database
        const updatedClub = clubId 
          ? await apiService.updateClub(clubId, { logo_url: logoUrl })
          : await apiService.updateCurrentClub({ logo_url: logoUrl });

        onClubUpdate(updatedClub);
        
        toast.show({
          title: "Club Logo",
          content: "Club logo updated successfully",
          duration: 3000,
          type: "success"
        });
      } catch (e: any) {
        toast.show({
          title: "Club Logo",
          content: "Failed to update club logo",
          duration: 5000,
          errorDetails: e,
          type: "fail"
        });
      }
    }
  };

  const handleCoverImageChange = async (file: File | null) => {
    if (file) {
      try {
        const coverImageUrl = await uploadImage(file, 'cover');
        
        // Update club in database
        const updatedClub = clubId 
          ? await apiService.updateClub(clubId, { cover_image_url: coverImageUrl })
          : await apiService.updateCurrentClub({ cover_image_url: coverImageUrl });
        onClubUpdate(updatedClub);

        toast.show({
          title: "Cover Image",
          content: "Cover image updated successfully",
          duration: 3000,
          type: "success"
        });
      } catch (e: any) {
        toast.show({
          title: "Cover Image",
          content: "Failed to update cover image",
          duration: 5000,
          errorDetails: e,
          type: "fail"
        });
      }
    }
  };

  const handleDescriptionChange = (content: string) => {
    setDescription(content);
    setHasChanges(true);
  };



  const uploadImage = async (file: File, type: 'logo' | 'cover'): Promise<string> => {
    try {
      const response = await apiService.uploadFile(file);
      
      if (typeof response === 'string') {
        return response;
      } else if (response && typeof response === 'object' && 'value' in response) {
        return (response as any).value;
      } else {
        throw new Error('Upload response did not contain a file URL');
      }
    } catch (error) {
      console.error(`Failed to upload ${type} image:`, error);
      throw error;
    }
  };

  const handleSave = async () => {
    setIsUpdating(true);
    
    try {
      // Update club description in database
      const updatedClub = clubId 
        ? await apiService.updateClub(clubId, { description })
        : await apiService.updateCurrentClub({ description });
      onClubUpdate(updatedClub);
      setHasChanges(false);
      
      toast.show({
        title: "Club Description",
        content: "Club description updated successfully",
        duration: 3000,
        type: "success"
      });

    } catch (e: any) {
      toast.show({
        title: "Club Description",
        content: "Failed to update club description",
        duration: 5000,
        errorDetails: e,
        type: "fail"
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="ClubSettings">
      {/* Logo Upload */}
      <div className="settingGroup">
        <ImageUploader
          label="Logo"
          currentImageUrl={club.logo_url}
          aspectRatio={1}
          circularCrop={true}
          onImageChange={handleLogoChange}
          maxWidth={200}
          maxHeight={200}
        />
        <small className="helpText">
          Upload a square logo that represents your club. It will be displayed as a circle.
        </small>
      </div>

      {/* Separator Line */}
      <div className="sectionSeparator"></div>

      {/* Cover Image Upload */}
      <div className="settingGroup">
        <ImageUploader
          label="Cover"
          currentImageUrl={club.cover_image_url}
          aspectRatio={31/10}
          circularCrop={false}
          onImageChange={handleCoverImageChange}
          maxWidth={1240}
          maxHeight={400}
          useTrueSize={true}
        />
        <small className="helpText">
          Upload a wide banner image for your club page header. Recommended size: 1240x400px.
        </small>
      </div>

      {/* Separator Line */}
      <div className="sectionSeparator"></div>

      {/* Description Editor */}
      <div className="descriptionSection">
        <div className="sectionHeader">
          <div className="sectionHeaderContent">
            <div>
              <h4>Description</h4>
              <p>Tell visitors about your club, history, mission, and activities</p>
            </div>
            <Button
              type="button"
              onClick={handleSave}
              disabled={!hasChanges}
              isLoading={isUpdating}
              className="sectionSaveButton"
            >
              Save Changes
            </Button>
          </div>
        </div>

        <div className="editorWrapper">
          <Editor
            key={club._id} // Force re-render when club changes
            apiKey={TINYMCE_API_KEY}
            onInit={(_evt, editor) => descriptionEditorRef.current = editor}
            init={{
              plugins: 'emoticons link lists searchreplace table visualblocks wordcount paste contextmenu',
              toolbar: 'undo redo | fontfamily fontsize | bold italic underline strikethrough | link | align lineheight | checklist numlist bullist indent outdent | emoticons | removeformat',
              block_unsupported_drop: true,
              paste_data_images: false,
              paste_enable_default_filters: true,
              paste_auto_cleanup_on_paste: true,
              paste_remove_styles: false,
              paste_remove_styles_if_webkit: false,
              contextmenu: 'cut copy paste | link table',
              automatic_uploads: false,
              images_upload_handler: () => {
                return Promise.reject("Image uploads are disabled.");
              },
              file_picker_types: "",
              menubar: false,
              branding: false,
              height: 300,
              setup: (editor) => {
                editor.on('init', () => {
                  editor.setContent(description || '');
                  const auxEl = document.querySelector('.tox-tinymce-aux') as HTMLElement | null;
                  if (auxEl) auxEl.style.setProperty('z-index', '2147483646', 'important');
                });
              }
            }}
            value={description}
            onEditorChange={handleDescriptionChange}
          />
        </div>
      </div>
    </div>
  );
};

export default ClubSettings;
